import React from "react";
import classnames from "classnames";
import * as actions from "../../actions/actions";
import { connect } from "react-redux";
import {
    EditorTransforms,
    ExportSizes,
    ExportSizeLabels,
    EditorBackgroundShapes,
    EditorBackgroundShapeNames,
    sign
} from "../../reducers/editor";
import ToolTip from "../tool-tip";
import Shapes from "./editor-backgrounds";
import ColorSelection from "./editor-color-selection";
import ColorPalette from "./color-palette";
import getQueryParam from "../../legacy/helpers/get_parameter_by_name";
import { isLightColor } from "../../legacy/helpers/get-color-brightness";

import { BLACK, WHITE } from "../../reducers/editor";

const renderBackgroundShape = ({ current, shapeType, onClick, className }) => {
    let PreviewComponent = Shapes[shapeType];
    if (!PreviewComponent) {
        return null;
    }
    let preview = (
        <svg>
            <PreviewComponent color="#00000" exportSize="75" checkered />
        </svg>
    );
    className = classnames(
        "editor-action-list-item",
        "editor-action-background-shape",
        className,
        `background-shape-${shapeType.toLowerCase()}`,
        {
            current: current == shapeType
        }
    );

    onClick =
        onClick &&
        onClick.bind(null, EditorTransforms.BACKGROUND_SHAPE, shapeType);
    return (
        <div key={shapeType} className={className} onClick={onClick}>
            <span className="editor-action-label">
                {EditorBackgroundShapeNames[shapeType]}
            </span>
            {preview}
        </div>
    );
};

const renderExportSize = ({ current, size, onClick, className }) => {
    className = classnames(
        "editor-action-list-item",
        "editor-action-export-size",
        className,
        {
            current: current == size
        }
    );
    onClick = onClick && onClick.bind(null, EditorTransforms.EXPORT_SIZE, size);
    let label = ExportSizeLabels[size];
    return (
        <div key={size} className={className} onClick={onClick}>
            <span>{size}px</span>
            <span className="label">{label}</span>
        </div>
    );
};

let toolTipProps = {
    interactive: false,
    className: "editor-action",
    tag: "div",
    theme: "dark editor",
    trigger: "mouseenter"
};

let interactiveToolTipProps = {
    theme: "dark editor",
    placement: "top",
    trigger: "click",
    className: "editor-action",
    distance: 10,
    delay: [1, 300],
    tag: "div"
};

const EditorControl = props => {
    let tipProps = {
        ...toolTipProps
    };

    if (props.children) {
        tipProps.className = null;
    }
    tipProps = {
        ...tipProps,
        ...props.tipProps
    };

    let control = (
        <ToolTip {...tipProps} target={props.control}>
            {props.helpText}
        </ToolTip>
    );

    if (!props.children) {
        return control;
    }

    let contentTipProps = {
        ...interactiveToolTipProps,
        ...props.contentTipProps
    };

    control = (
        <ToolTip {...contentTipProps} target={control}>
            {props.children}
        </ToolTip>
    );

    return control;
};

export const ExportSizesControl = props => {
    let { exportSize, editorTransform } = props;
    let exportSizes = ExportSizes.map(size => {
        return renderExportSize({
            current: exportSize,
            size,
            onClick: editorTransform
        });
    });

    let control =
        props.control ||
        renderExportSize({
            size: exportSize
        });

    return (
        <EditorControl
            helpText="export size"
            control={control}
            contentTipProps={{
                className: "editor-action"
            }}
        >
            <div className="editor-action-list editor-export-size-list">
                {exportSizes}
            </div>
        </EditorControl>
    );
};

class NounProjectIconEditor extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
    }

    setBackgroundColor = hex => {
        return this.props.editorTransform(
            EditorTransforms.BACKGROUND_COLOR,
            hex
        );
    };
    setIconColor = hex => {
        return this.props.editorTransform(EditorTransforms.ICON_COLOR, hex);
    };
    render() {
        let className = classnames("nounproject-icon-editor-controls");
        let {
            scaleX,
            scaleY,
            backgroundShape,
            backgroundColor,
            color,
            exportSize,
            rotate,
            palette,
            editorAddColor
        } = this.props;

        let reset = (
            <div className="reset link-action" onClick={this.props.editorReset}>
                reset
            </div>
        );

        let rotateCCW = (
            <EditorControl
                control={
                    <div
                        className="editor-icon editor-icon-rotate editor-icon-rotate-counterclockwise"
                        onClick={this.props.editorTransform.bind(
                            null,
                            EditorTransforms.ROTATE_CCW
                        )}
                    />
                }
                helpText="rotate counter-clockwise"
            />
        );

        let rotateIndicator = (
            <EditorControl
                control={
                    <div
                        className="editor-rotate-indicator"
                        onClick={this.props.editorTransform.bind(
                            null,
                            EditorTransforms.SET_ROTATION,
                            0
                        )}
                    >
                        {(rotate % 360) * sign(scaleX) * sign(scaleY)}&deg;
                    </div>
                }
                helpText="reset rotation"
            />
        );

        let rotateCW = (
            <EditorControl
                control={
                    <div
                        className="editor-icon editor-icon-rotate editor-icon-rotate-clockwise"
                        onClick={this.props.editorTransform.bind(
                            null,
                            EditorTransforms.ROTATE_CW
                        )}
                    />
                }
                helpText="rotate clockwise"
            />
        );

        let flipHorizontalClassname = classnames(
            "editor-icon",
            "editor-icon-flip editor-icon-flip-horizontal",
            sign(scaleX) < 0 ? "" : "flipped"
        );

        let flipHorizontalIcon = (
            <div
                className={flipHorizontalClassname}
                onClick={this.props.editorTransform.bind(
                    null,
                    EditorTransforms.FLIP_HORIZONTAL
                )}
            />
        );

        let flipHorizontal = (
            <EditorControl
                control={flipHorizontalIcon}
                helpText="flip horizontal"
            />
        );

        let flipVerticalClassname = classnames(
            "editor-icon",
            "editor-icon-flip editor-icon-flip-vertical",
            sign(scaleY) < 0 ? "" : "flipped"
        );

        let flipVerticalIcon = (
            <div
                className={flipVerticalClassname}
                onClick={this.props.editorTransform.bind(
                    null,
                    EditorTransforms.FLIP_VERTICAL
                )}
            />
        );

        let flipVertical = (
            <EditorControl
                control={flipVerticalIcon}
                helpText="flip vertical"
            />
        );

        let iconColorSelection = (
            <EditorControl
                helpText={`icon color ${color}`}
                control={<ColorSwatch color={color} />}
                tipProps={{ maxWidth: null }}
                contentTipProps={{
                    className: "editor-action editor-action-color-picker",
                    maxWidth: null,
                    onHide: () => editorAddColor(color)
                }}
            >
                <ColorSelection
                    color={color}
                    palette={palette}
                    setColor={this.setIconColor}
                    editorSetPaletteMode={this.props.editorSetPaletteMode}
                    paletteMode={this.props.paletteMode}
                />
            </EditorControl>
        );

        let backgroundColorSelection = (
            <EditorControl
                helpText={`background color ${backgroundColor}`}
                control={<ColorSwatch color={backgroundColor} />}
                tipProps={{ maxWidth: null }}
                contentTipProps={{
                    className: "editor-action editor-action-color-picker",
                    maxWidth: null,
                    onHide: () => editorAddColor(backgroundColor)
                }}
            >
                <ColorSelection
                    color={backgroundColor}
                    palette={palette}
                    setColor={this.setBackgroundColor}
                    editorSetPaletteMode={this.props.editorSetPaletteMode}
                    paletteMode={this.props.paletteMode}
                />
            </EditorControl>
        );

        let backgrounds = Object.entries(EditorBackgroundShapes).map(
            ([shapeType]) => {
                return renderBackgroundShape({
                    current: backgroundShape,
                    shapeType,
                    onClick: this.props.editorTransform
                });
            }
        );

        backgrounds = (
            <EditorControl
                helpText="shape"
                control={renderBackgroundShape({
                    shapeType: backgroundShape,
                    className: "editor-no-label"
                })}
                contentTipProps={{
                    className:
                        "editor-action editor-action-background-shape-container"
                }}
            >
                <div className="editor-action-list">{backgrounds}</div>
            </EditorControl>
        );

        return (
            <div className={className}>
                <div className="editor-controls">
                    <div className="editor-control-group">{reset}</div>

                    <div className="editor-control-group">
                        {rotateCCW}
                        {rotateIndicator}
                        {rotateCW}
                        {flipVertical}
                        {flipHorizontal}
                    </div>

                    <div className="editor-control-group">
                        {iconColorSelection}
                        {backgroundColorSelection}
                        {backgrounds}
                    </div>
                </div>
            </div>
        );
    }
}

const NounProjectIconEditorWrapper = props => {
    return props.legacyEditor ? (
        <LegacyEditor {...props} />
    ) : (
        <NounProjectIconEditor {...props} />
    );
};

const ColorSwatch = props => {
    let style = {
        backgroundColor: props.color
    };
    let className = classnames(
        "editor-color-swatch",
        props.color && `color-${props.color.replace("#", "")}`,
        {
            "light-color": isLightColor(props.color)
        }
    );
    return <div className={className} style={style} />;
};

function mapStateToProps(state, props) {
    let { editor } = state;
    let { palette, paletteMode } = editor;
    let {
        scaleX,
        scaleY,
        backgroundColor,
        backgroundShape,
        color,
        exportSize,
        rotate
    } = editor.transforms;

    return {
        scaleX,
        scaleY,
        backgroundColor,
        backgroundShape,
        color,
        exportSize,
        rotate,
        palette,
        paletteMode
    };
}

class LegacyEditor extends React.Component {
    onPickerClose = () => {
        this.props.editorAddColor(this.props.color);
    };

    setColor = color => {
        this.props.editorTransform(EditorTransforms.ICON_COLOR, color);
    };

    render() {
        let customColorSwatch = (
            <div
                key="key-custom-color"
                title="Change color to a custom color"
                className={`color ui_plus`}
                style={{ backgroundColor: "#BABABA" }}
            />
        );

        let presets = [BLACK, WHITE];

        let customColorUI = (
            <EditorControl
                control={customColorSwatch}
                helpText="new color"
                contentTipProps={{
                    maxWidth: null,
                    onHide: this.onPickerClose,
                    className: "editor-action editor-action-color-picker"
                }}
            >
                <ColorSelection
                    color={this.props.color}
                    setColor={this.setColor}
                    editorSetPaletteMode={this.props.editorSetPaletteMode}
                    paletteMode={this.props.paletteMode}
                />
            </EditorControl>
        );

        return (
            <div className="nounproject-icon-editor-controls hero-color-picker legacy-editor">
                <ColorPalette
                    presets={presets}
                    palette={this.props.palette.filter(
                        c => !presets.includes(c)
                    )}
                    setColor={this.setColor}
                >
                    {customColorUI}
                </ColorPalette>
            </div>
        );
    }
}

export default connect(mapStateToProps, {
    setColor: actions.setColor,
    editorReset: actions.editorReset,
    editorExport: actions.editorExport,
    editorTransform: actions.editorTransform,
    editorAddColor: actions.editorAddColor,
    editorSetPaletteMode: actions.editorSetPaletteMode
})(NounProjectIconEditorWrapper);
